import { Injectable, Signal, computed, signal } from '@angular/core';
import { AccountStatus, Company } from '@/shared/models/company';

export interface LabelData {
  artist: string;
  title: string;
  genre: string;
  year: string;
  country: string;
  label: string;
  catalogNumber: string;
  barcode: string;
  releaseId: string;
  mediaCondition: string;
  sleeveCondition: string;
  price: string;
  location: string;
  url: string;
  /** CRAT-1331 — short Inventory item ID (empty for items the backfill has not reached). */
  inventoryId: string;
}

export interface PrintResult {
  success: boolean;
  message?: string;
}

export interface DiscogsMarketplaceListing {
  price: string;
  mediaCondition: string;
  sleeveCondition: string;
  comment?: string;
  sellerRating: number | null;
  sellerRatingCount: number | null;
  shipsFrom: string;
}

export interface DiscogsMarketplaceStatistics {
  have?: number;
  want?: number;
  avgRating?: number | null;
  ratingCount?: number;
  lastSold?: string;
  lowest?: string;
  median?: string;
  highest?: string;
}

export interface DiscogsMarketplaceResult {
  listings: DiscogsMarketplaceListing[];
  totalListings: number;
  statistics?: DiscogsMarketplaceStatistics;
  error?: string;
}

export interface DiscogsSale {
  orderDate: string;
  mediaCondition: string;
  sleeveCondition: string;
  /** Price in the logged-in user's currency, with their currency symbol (e.g. "€65.25"). */
  priceInUserCurrency: string;
  /** Original price in the seller's currency, with their currency symbol (e.g. "$75.00"). */
  originalPrice: string;
  /** Optional buyer-visible comment from the seller at time of sale. */
  comment?: string;
}

export interface DiscogsSalesHistoryResult {
  sales: DiscogsSale[];
  totalSales: number;
  error?: string;
}

export type BulkProgressStatus = 'idle' | 'running' | 'done' | 'failed' | 'cancelled';

export interface BulkProgress {
  total: number;
  completed: number;
  currentIndex: number;
  lastError?: { index: number; message: string };
  status: BulkProgressStatus;
}

export interface BulkPrintResult {
  ok: boolean;
  printedCount: number;
  failedAt?: number;
  error?: string;
  printerUsed?: string;
}

export interface BulkPrintHandle {
  progress: Signal<BulkProgress>;
  result: Promise<BulkPrintResult>;
  cancel: () => void;
}

const BULK_PRINT_MIN_VERSION = '1.5.0';
const BULK_PRINT_CLIENT_VERSION = '1.5.0';

const getExtensionId = (): string | null => document.documentElement.getAttribute('data-cratebase-ext-id');

/** CRAT-1283 — how long a customer confirmation stays valid in the extension (renewed on every app load). */
const CUSTOMER_CONFIRMATION_DAYS = 30;
/** The extension's content script tags the page shortly after load; retry finding it for a few seconds. */
const EXTENSION_LOOKUP_ATTEMPTS = 5;
const EXTENSION_LOOKUP_INTERVAL_MS = 1000;

/**
 * CRAT-1283 — whether a company counts as a Cratebase customer for extension
 * member features (label templates, the "List on Cratebase" button), and until when. Paying (Active)
 * accounts get a rolling 30 days; trials (Pending) are capped at their trial
 * end so an unconverted trial loses the perk on time. Accounts without a status
 * predate account statuses and are Active by API default.
 */
export const cratebaseCustomerWindow = (
  company: Company | null | undefined,
  now = Date.now()
): { customer: boolean; validUntil: string | null } => {
  const status = company?.accountStatus ?? AccountStatus.Active;
  if (!company || (status !== AccountStatus.Active && status !== AccountStatus.Pending)) {
    return { customer: false, validUntil: null };
  }
  let until = now + CUSTOMER_CONFIRMATION_DAYS * 24 * 60 * 60 * 1000;
  if (status === AccountStatus.Pending && company.trialEndDate) {
    const trialEnd = new Date(company.trialEndDate).getTime();
    if (Number.isFinite(trialEnd)) until = Math.min(until, trialEnd);
  }
  return until > now
    ? { customer: true, validUntil: new Date(until).toISOString() }
    : { customer: false, validUntil: null };
};

const parseVersion = (v: string): number[] =>
  v
    .split('.')
    .map((part) => Number.parseInt(part, 10))
    .map((n) => (Number.isFinite(n) ? n : 0));

/** Returns true when `a >= b` for `MAJOR.MINOR.PATCH` strings. Tolerant of missing parts. */
const versionGte = (a: string | null, b: string): boolean => {
  if (!a) return false;
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const left = pa[i] ?? 0;
    const right = pb[i] ?? 0;
    if (left > right) return true;
    if (left < right) return false;
  }
  return true;
};

@Injectable({ providedIn: 'root' })
export class CratebaseExtensionService {
  isInstalled = signal(false);
  /** Plugin version, populated by the latest `ping` response. Null until we've heard back. */
  pluginVersion = signal<string | null>(null);
  /** Bulk print requires plugin >= 1.5.0 (port: 'bulk-print'). */
  supportsBulkPrint = computed(() => this.isInstalled() && versionGte(this.pluginVersion(), BULK_PRINT_MIN_VERSION));

  /** One in-flight bulk-print batch per tab. */
  #bulkInFlight = false;

  /** Last account status sent to the extension this page session (dedupes user$ re-emissions). */
  #lastAccountReport: string | null = null;

  /**
   * CRAT-1283 — tell the extension whether this browser belongs to a Cratebase
   * member, so it can unlock label templates and the "List on Cratebase" button. Called on
   * every user (re)load; no-op when the extension isn't installed. Older
   * extension versions ignore the unknown action.
   */
  reportAccountStatus = (company: Company | null | undefined, attempt = 1): void => {
    if (!company) return;
    const extensionId = getExtensionId();
    if (!extensionId) {
      if (attempt < EXTENSION_LOOKUP_ATTEMPTS) {
        setTimeout(() => this.reportAccountStatus(company, attempt + 1), EXTENSION_LOOKUP_INTERVAL_MS);
      }
      return;
    }
    if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) return;

    const data = cratebaseCustomerWindow(company);
    const key = `${extensionId}|${company.accountStatus}|${company.trialEndDate ?? ''}`;
    if (key === this.#lastAccountReport) return;
    this.#lastAccountReport = key;

    try {
      chrome.runtime.sendMessage!(extensionId, { action: 'setCratebaseAccount', data }, () => {
        // Read lastError so older extensions (no handler) don't log an unchecked error
        void chrome.runtime?.lastError;
      });
    } catch {
      this.#lastAccountReport = null;
    }
  };

  checkInstalled = (): boolean => {
    const installed = !!getExtensionId();
    this.isInstalled.set(installed);
    if (installed) {
      // Fire-and-forget — supportsBulkPrint will flip when the response lands.
      this.#pingVersion();
    } else {
      this.pluginVersion.set(null);
    }
    return installed;
  };

  printLabel = (data: LabelData): Promise<PrintResult> =>
    new Promise((resolve) => {
      const extensionId = getExtensionId();
      if (!extensionId || typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
        resolve({ success: false, message: 'Chrome extension not available' });
        return;
      }

      try {
        chrome.runtime.sendMessage!(extensionId, { action: 'printLabel', data }, (response) => {
          if (chrome.runtime?.lastError) {
            resolve({ success: false, message: chrome.runtime.lastError.message });
            return;
          }
          const result = response ?? { success: false, message: 'No response from extension' };
          if (!result.message && result.error) {
            result.message = result.error;
          }
          resolve(result);
        });
      } catch (error) {
        resolve({ success: false, message: String(error) });
      }
    });

  /**
   * Print a batch of labels through the plugin's long-lived bulk-print port.
   * The plugin prints them one at a time, posts per-item progress, and stops
   * on the first error. Caller can `cancel()` to abort mid-batch — the plugin
   * disconnects and the result resolves with `{ ok: false, error: 'cancelled' }`.
   *
   * Only one bulk-print batch may be in-flight per tab; concurrent calls throw.
   * See CRAT-1065 for the wire protocol.
   */
  printLabels = (items: LabelData[]): BulkPrintHandle => {
    if (this.#bulkInFlight) {
      throw new Error('A bulk print is already in progress in this tab');
    }
    const extensionId = getExtensionId();
    if (!extensionId || typeof chrome === 'undefined' || !chrome.runtime?.connect) {
      const progress = signal<BulkProgress>({
        total: items.length,
        completed: 0,
        currentIndex: 0,
        status: 'failed',
        lastError: { index: 0, message: 'Chrome extension not available' },
      });
      return {
        progress,
        result: Promise.resolve({ ok: false, printedCount: 0, error: 'Chrome extension not available' }),
        cancel: () => undefined,
      };
    }

    this.#bulkInFlight = true;
    const progress = signal<BulkProgress>({
      total: items.length,
      completed: 0,
      currentIndex: 0,
      status: 'running',
    });
    let port: chrome.runtime.Port | null = null;
    let terminated = false;
    let cancelRequested = false;
    let resolveResult!: (value: BulkPrintResult) => void;
    const result = new Promise<BulkPrintResult>((resolve) => {
      resolveResult = resolve;
    });

    const finalize = (next: BulkProgress, value: BulkPrintResult) => {
      if (terminated) return;
      terminated = true;
      progress.set(next);
      this.#bulkInFlight = false;
      resolveResult(value);
      try {
        port?.disconnect();
      } catch {
        // Already disconnected.
      }
    };

    try {
      port = chrome.runtime.connect(extensionId, { name: 'bulk-print' });
    } catch (error) {
      finalize(
        { ...progress(), status: 'failed', lastError: { index: 0, message: String(error) } },
        { ok: false, printedCount: 0, error: String(error) }
      );
      return { progress, result, cancel: () => undefined };
    }

    port.onMessage.addListener((message: BulkPortMessage) => {
      switch (message.type) {
        case 'accepted': {
          progress.update((p) => ({ ...p, total: message.total ?? p.total, status: 'running' }));
          return;
        }
        case 'progress': {
          const idx = message.index ?? 0;
          if (message.status === 'printing') {
            progress.update((p) => ({ ...p, currentIndex: idx, status: 'running' }));
          } else if (message.status === 'printed') {
            progress.update((p) => ({ ...p, currentIndex: idx, completed: p.completed + 1, status: 'running' }));
          }
          return;
        }
        case 'done': {
          const printedCount = message.printedCount ?? progress().completed;
          finalize({ ...progress(), completed: printedCount, status: 'done' }, { ok: true, printedCount });
          return;
        }
        case 'failed': {
          const failedAt = message.failedAt ?? progress().currentIndex;
          const printedCount = message.printedCount ?? progress().completed;
          const error = message.error ?? 'Print failed';
          finalize(
            {
              ...progress(),
              completed: printedCount,
              currentIndex: failedAt,
              status: 'failed',
              lastError: { index: failedAt, message: error },
            },
            { ok: false, printedCount, failedAt, error }
          );
          return;
        }
        case 'rejected': {
          const reason = message.reason ?? 'rejected';
          finalize(
            { ...progress(), status: 'failed', lastError: { index: 0, message: reason } },
            { ok: false, printedCount: 0, error: reason }
          );
          return;
        }
        default:
          return;
      }
    });

    port.onDisconnect.addListener(() => {
      if (terminated) return;
      // Plugin disconnected without a terminal message — likely a crash or the user
      // disabled the extension mid-batch. Surface as a failure so the UI can offer Retry.
      if (cancelRequested) {
        finalize(
          { ...progress(), status: 'cancelled' },
          { ok: false, printedCount: progress().completed, failedAt: progress().currentIndex, error: 'cancelled' }
        );
        return;
      }
      const lastErr = chrome.runtime?.lastError?.message;
      finalize(
        {
          ...progress(),
          status: 'failed',
          lastError: { index: progress().currentIndex, message: lastErr ?? 'plugin_disconnected' },
        },
        {
          ok: false,
          printedCount: progress().completed,
          failedAt: progress().currentIndex,
          error: lastErr ?? 'plugin_disconnected',
        }
      );
    });

    try {
      port.postMessage({ type: 'start', items, clientVersion: BULK_PRINT_CLIENT_VERSION });
    } catch (error) {
      finalize(
        { ...progress(), status: 'failed', lastError: { index: 0, message: String(error) } },
        { ok: false, printedCount: 0, error: String(error) }
      );
    }

    return {
      progress,
      result,
      cancel: () => {
        if (terminated) return;
        cancelRequested = true;
        try {
          port?.disconnect();
        } catch {
          // disconnect listener handles finalisation
        }
      },
    };
  };

  /**
   * Fetches a release's full sales history from Discogs via the plugin.
   * Plugin scrapes https://www.discogs.com/sell/history/{releaseId} server-side
   * (its origin) and returns parsed sales. CRAT-963.
   */
  fetchSalesHistory = (releaseId: string): Promise<DiscogsSalesHistoryResult> =>
    new Promise((resolve) => {
      const emptyResult: DiscogsSalesHistoryResult = { sales: [], totalSales: 0 };
      const extensionId = getExtensionId();
      if (!extensionId || typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
        resolve({ ...emptyResult, error: 'Chrome extension not available' });
        return;
      }

      try {
        chrome.runtime.sendMessage!(extensionId, { action: 'fetchSalesHistory', data: { releaseId } }, (response) => {
          if (chrome.runtime?.lastError) {
            resolve({ ...emptyResult, error: chrome.runtime.lastError.message });
            return;
          }
          if (!response?.success) {
            resolve({ ...emptyResult, error: response?.error ?? 'No response from extension' });
            return;
          }
          resolve({
            sales: response.data?.sales ?? [],
            totalSales: response.data?.totalSales ?? 0,
          });
        });
      } catch (error) {
        resolve({ ...emptyResult, error: String(error) });
      }
    });

  fetchMarketplaceListings = (releaseId: string): Promise<DiscogsMarketplaceResult> =>
    new Promise((resolve) => {
      const emptyResult: DiscogsMarketplaceResult = { listings: [], totalListings: 0 };
      const extensionId = getExtensionId();
      if (!extensionId || typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
        resolve({ ...emptyResult, error: 'Chrome extension not available' });
        return;
      }

      try {
        chrome.runtime.sendMessage!(
          extensionId,
          { action: 'fetchMarketplaceListings', data: { releaseId } },
          (response) => {
            if (chrome.runtime?.lastError) {
              resolve({ ...emptyResult, error: chrome.runtime.lastError.message });
              return;
            }
            if (!response?.success) {
              resolve({ ...emptyResult, error: response?.error ?? 'No response from extension' });
              return;
            }
            resolve({
              listings: response.data?.listings ?? [],
              totalListings: response.data?.totalListings ?? 0,
              statistics: response.data?.statistics,
            });
          }
        );
      } catch (error) {
        resolve({ ...emptyResult, error: String(error) });
      }
    });

  #pingVersion = (): void => {
    const extensionId = getExtensionId();
    if (!extensionId || typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
      return;
    }
    try {
      chrome.runtime.sendMessage!(extensionId, { action: 'ping' }, (response) => {
        if (chrome.runtime?.lastError) {
          this.pluginVersion.set(null);
          return;
        }
        if (response?.success && typeof response.version === 'string') {
          this.pluginVersion.set(response.version);
        } else {
          this.pluginVersion.set(null);
        }
      });
    } catch {
      this.pluginVersion.set(null);
    }
  };
}

type BulkPortMessage =
  | { type: 'accepted'; total?: number }
  | { type: 'progress'; index?: number; status?: 'printing' | 'printed'; printer?: string }
  | { type: 'done'; printedCount?: number }
  | { type: 'failed'; failedAt?: number; printedCount?: number; error?: string }
  | { type: 'rejected'; reason?: 'unauthorized' | 'unsupported' | string };
